import { refId } from "@suse-o11y/ref-id";
import { effect, untracked, useComputed } from "@suse-o11y/signal";
import React from "react";

import { DragAndDrop } from "~/components/drag-and-drop";
import { useDraggableContext } from "~/components/drag-and-drop/context/draggable";
import { Draggable } from "~/components/drag-and-drop/draggable";
import { DraggablePlaceholder } from "~/components/drag-and-drop/draggable-placeholder";
import { Droppable } from "~/components/drag-and-drop/droppable";
import { Table } from "~/components/table";
import { TableBody } from "~/components/table/body";
import { TableHead } from "~/components/table/head";
import { TableHeadCell } from "~/components/table/head-cell";
import { groupBy } from "~/tools/group-by";
import { isPresent } from "~/tools/is-present";
import { map } from "~/tools/map";
import { pickBy } from "~/tools/pick-by";
import { pipe } from "~/tools/pipe";
import { prop } from "~/tools/prop";
import { px } from "~/tools/px";
import { reorder } from "~/tools/reorder";
import { splice } from "~/tools/splice";
import { toArray } from "~/tools/to-array";
import { toKeys } from "~/tools/to-keys";

import { OBSERVABILITY_PROMQL_VARIABLES } from "../../routes/dashboards/built-in-variables";
import { useExplorationDrawerState } from "../react/drawer/use-exploration-drawer-state";

import type { VariableState, VariablesState } from "./_drawer-state";
import { useFormTimeRange } from "./_form-context";
import { VariableDragHandle } from "./_variable-drag-handle";
import { VariableRow } from "./_variable-row";

const OBSERVABILITY_PROMQL_VARIABLES_SET = new Set<string>(OBSERVABILITY_PROMQL_VARIABLES);

function useValidateVariables(state: VariablesState): void {
	React.useEffect(
		() =>
			effect((monitor) => {
				const variables = state.getElements(monitor);

				const duplicates = pipe(
					variables,
					map((variable) => variable.getInput(monitor)),
					groupBy(prop("name")),
					pickBy((list) => list.length > 1),
					toKeys(),
				);

				for (const variable of variables) {
					const name = variable.getInput(monitor).name.trim();
					const error = duplicates.includes(name)
						? "Duplicated variable name"
						: OBSERVABILITY_PROMQL_VARIABLES_SET.has(name)
							? "Built-in variables cannot be overridden"
							: null;

					variable.setError(error);
				}
			}),
		[state],
	);
}

export interface VariablesTableProps {
	id?: string;
	variables: VariablesState;
}

export const VariablesTable: React.FC<VariablesTableProps> = ({ variables: state, id }) => {
	const explorationDrawer = useExplorationDrawerState();
	const timeRange = useFormTimeRange();

	const droppableId = React.useId();
	const firstRowRef = React.useRef<HTMLDivElement>(null);
	const [gridTemplateColumns, setGridTemplateColumns] = React.useState<string>();
	const variables = useComputed((monitor) => state.getElements(monitor), [state]);

	useValidateVariables(state);

	return (
		<DragAndDrop
			onBeforeDragStart={() => {
				// persist the columns width before dragging starts so the table layout stays consistent during d&d
				if (firstRowRef.current) {
					const columns = toArray(firstRowRef.current.children).map((column) => {
						/**
						 * The .getBoundingClientRect() should be used instead of .clientWidth because it returns a fractional value
						 * which is more precise and prevents layout shifts when the width is not an integer.
						 */
						const { width } = column.getBoundingClientRect();

						return px(width);
					});

					setGridTemplateColumns(columns.join(" "));
				}
			}}
			onDragEnd={({ source, destination }) => {
				state.setElements(reorder(variables, source.index, destination?.index ?? source.index));
				setGridTemplateColumns(undefined);
			}}
		>
			<Table // color the background so it contrasts when d&d
				className="grid-cols-[auto_auto_auto_max-content_minmax(200px,1fr)_auto] bg-grey-300"
				style={{ gridTemplateColumns }}
			>
				<TableHead>
					<TableHeadCell className="col-start-2">Name</TableHeadCell>
					<TableHeadCell>Display Label</TableHeadCell>
					<TableHeadCell>Type</TableHeadCell>
					<TableHeadCell>Description</TableHeadCell>
				</TableHead>

				<Droppable
					asChild
					direction="vertical"
					draggableClone={<ClonedVariableRow gridTemplateColumns={gridTemplateColumns} variables={variables} />}
					droppableId={droppableId}
				>
					<TableBody each={variables}>
						{(variable, index) => (
							<Draggable asChild index={index} isDragDisabled={variables.length < 2} key={refId(variable)}>
								<VariableRow
									handle={variables.length > 1 ? <VariableDragHandle /> : null}
									onDelete={() => {
										state.setInput(splice(index, 1, []));
									}}
									onEdit={() => {
										untracked((monitor) => {
											explorationDrawer.openEditDashboardVariable({
												id: `${id}_variable_${variable.getInput(monitor).name}`,
												variable: variable.getInput(monitor),
												// pass only valid variables
												variables: state.getOutput(monitor)?.filter(isPresent) ?? [],
												timeRange,
												onSave: (updated) => {
													variable.setInput(updated);
												},
											});
										});
									}}
									ref={index === 0 ? firstRowRef : undefined}
									variable={variable}
								/>
							</Draggable>
						)}

						<DraggablePlaceholder />
					</TableBody>
				</Droppable>
			</Table>
		</DragAndDrop>
	);
};

const ClonedVariableRow = React.forwardRef<
	HTMLDivElement,
	{
		variables: ReadonlyArray<VariableState>;
		gridTemplateColumns: undefined | string;
		style?: React.CSSProperties;
	}
>(({ variables, gridTemplateColumns, style, ...props }, ref) => {
	const { source } = useDraggableContext();

	return (
		<VariableRow
			handle={<VariableDragHandle />}
			ref={ref}
			style={{ gridTemplateColumns, ...style }}
			variable={variables[source.index]}
			{...props}
		/>
	);
});
